import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Aurora from '../components/Aurora';
import { DOWNLOAD_LINKS } from '../lib/downloadLinks';
import {
  ShieldCheck,
  Zap,
  BellRing,
  Monitor,
  ArrowRight,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Play,
  RotateCcw,
  Volume2,
  Sparkles,
  Lock,
  Layers,
  Activity,
  ChevronDown,
  ChevronUp,
  Mail,
  Send,
  Code2,
  KeyRound,
  Building2,
  Clock,
  Radio,
  Check,
  Copy,
  Download,
  Cpu,
  FileCheck,
  Server,
  HardDrive,
  Terminal,
  EyeOff,
  Calculator,
  Smartphone,
} from 'lucide-react';
import pulseLogo from '../assets/pulse-logo.png';

const SPY_ALTERNATIVES = [
  'invasive spying.',
  'creepy webcams.',
  'secret keyloggers.',
  'screen recording.',
  'stealth tracking.',
];

export function LandingPage() {
  const { user } = useAuth();

  // Option 2 Pricing State
  const [pricingCurrency, setPricingCurrency] = useState<'USD' | 'INR'>('USD');
  const [pricingBilling, setPricingBilling] = useState<'ANNUAL' | 'MONTHLY'>('ANNUAL');
  const [calculatorSeats, setCalculatorSeats] = useState(15);

  // Interactive Simulator State
  const [simStatus, setSimStatus] = useState<'ACTIVE' | 'INACTIVE' | 'OFFLINE'>('ACTIVE');
  const [idleSeconds, setIdleSeconds] = useState(14);
  const [thresholdSeconds, setThresholdSeconds] = useState(180);
  const [showToast, setShowToast] = useState(false);
  const [soundPlaying, setSoundPlaying] = useState(false);

  // Active channel tab in preview
  const [activeChannelTab, setActiveChannelTab] = useState<'TELEGRAM' | 'EMAIL' | 'WEBHOOK'>('TELEGRAM');

  // FAQ accordion state
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // Copy PowerShell download command state
  const [copiedCommand, setCopiedCommand] = useState(false);

  // Animated hero headline word rotation
  const [spyIndex, setSpyIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSpyIndex((prev) => (prev + 1) % SPY_ALTERNATIVES.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  // Simulator timer effect
  useEffect(() => {
    let interval: any;
    if (simStatus === 'ACTIVE') {
      interval = setInterval(() => {
        setIdleSeconds((prev) => {
          const next = prev + 1;
          if (next >= thresholdSeconds) {
            setSimStatus('INACTIVE');
            setShowToast(true);
          }
          return next;
        });
      }, 1000);
    } else if (simStatus === 'INACTIVE') {
      interval = setInterval(() => {
        setIdleSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [simStatus, thresholdSeconds]);

  // Studio-Quality Harmonic Bell Chime (Web Audio API)
  const playAlertSound = () => {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      const ctx = new AudioContextClass();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      setSoundPlaying(true);
      setTimeout(() => setSoundPlaying(false), 950);

      const now = ctx.currentTime;
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.35, now);
      masterGain.connect(ctx.destination);

      // Play a layered chime tone with fundamental sine wave + harmonic overtone
      const playBellNote = (freq: number, startTime: number, duration: number, volume: number) => {
        // Fundamental Tone (Sine)
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(freq, startTime);

        gain1.gain.setValueAtTime(0.001, startTime);
        gain1.gain.linearRampToValueAtTime(volume, startTime + 0.015);
        gain1.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

        osc1.connect(gain1);
        gain1.connect(masterGain);
        osc1.start(startTime);
        osc1.stop(startTime + duration);

        // Harmonic Overtone (Triangle) - provides warm crystalline sparkle
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'triangle';
        osc2.frequency.setValueAtTime(freq * 2, startTime);

        gain2.gain.setValueAtTime(0.001, startTime);
        gain2.gain.linearRampToValueAtTime(volume * 0.3, startTime + 0.012);
        gain2.gain.exponentialRampToValueAtTime(0.0001, startTime + duration * 0.7);

        osc2.connect(gain2);
        gain2.connect(masterGain);
        osc2.start(startTime);
        osc2.stop(startTime + duration);
      };

      // 3-Stage Cascading Harmonic Bell: D5 -> A5 -> E6
      playBellNote(587.33, now, 0.5, 0.45);        // Warm opening note
      playBellNote(880.00, now + 0.11, 0.8, 0.55); // Rich resolving bell note
      playBellNote(1318.51, now + 0.22, 0.6, 0.28);// Crystal shimmer overtone
    } catch {
      // AudioContext unavailable or blocked by browser policy
    }
  };

  const handleSimulateInactivity = () => {
    setIdleSeconds(thresholdSeconds + 12);
    setSimStatus('INACTIVE');
    setShowToast(true);
    playAlertSound();
  };

  const handleSimulateActivity = () => {
    setIdleSeconds(0);
    setSimStatus('ACTIVE');
    setShowToast(false);
  };

  const formatSeconds = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const s = sec % 60;
    return `${mins}:${s.toString().padStart(2, '0')}`;
  };

  const getThresholdLabel = (sec: number) => {
    if (sec < 60) return `${sec} seconds`;
    if (sec === 60) return '1 minute (60s)';
    if (sec % 60 === 0) return `${sec / 60} minutes`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  };

  return (
    <div className="min-h-screen bg-[#070707] text-white selection:bg-pulse-blue selection:text-white font-sans antialiased relative overflow-x-hidden">
      {/* Live WebGL Fluid Aurora (React Bits) */}
      <div className="absolute top-0 left-0 right-0 h-[650px] sm:h-[800px] overflow-hidden pointer-events-none z-0 opacity-45">
        <Aurora
          colorStops={["#3A29FF", "#4F7CFF", "#00F5D4"]}
          blend={0.55}
          amplitude={1.15}
          speed={0.5}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#070707]/40 to-[#070707]" />
      </div>

      {/* Cyber Grid Layer */}
      <div className="absolute inset-0 cyber-grid pointer-events-none z-0" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[300px] bg-gradient-to-b from-blue-500/10 via-indigo-500/5 to-transparent blur-3xl pointer-events-none z-0" />

      {/* 1. TOP ECOSYSTEM RIBBON */}
      <div className="w-full bg-gradient-to-r from-blue-950/40 via-pulse-blue/15 to-indigo-950/40 border-b border-pulse-blue/20 px-4 py-2 text-center text-xs text-zinc-300 relative z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 flex-wrap">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-pulse-blue/20 text-pulse-blue border border-pulse-blue/30">
            CK Ecosystem
          </span>
          <span>
            CK Pulse is a core platform of the <strong>CK Ecosystem</strong> suite.
          </span>
          <a
            href="https://ck-ecosystem.pages.dev/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-semibold text-white hover:text-pulse-blue transition-colors underline underline-offset-4 decoration-pulse-blue/60"
          >
            Explore ck-ecosystem.pages.dev
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* 2. FLOATING GLASS NAVBAR */}
      <nav className="sticky top-4 z-40 max-w-6xl mx-auto px-4 sm:px-6">
        <div className="glass-nav rounded-2xl px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-4 shadow-2xl shadow-black/60">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <img src={pulseLogo} alt="CK Pulse" className="w-8 h-8 object-contain shrink-0 drop-shadow-md group-hover:scale-105 transition-transform" />
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="font-bold tracking-tight text-sm sm:text-base text-white">CK Pulse</span>
              <span className="hidden sm:inline-block px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold uppercase bg-zinc-800 text-zinc-400 border border-zinc-700">
                v2.0
              </span>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden xl:flex items-center gap-6 text-xs text-zinc-400 font-medium shrink-0 whitespace-nowrap">
            <a href="#simulator" className="hover:text-white transition-colors">Live Demo</a>
            <a href="#pillars" className="hover:text-white transition-colors">Features</a>
            <a href="#privacy" className="hover:text-white transition-colors">Privacy Pledge</a>
            <a href="#architecture" className="hover:text-white transition-colors">Architecture</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a
              href="https://ck-ecosystem.pages.dev/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-pulse-blue hover:text-white transition-colors font-medium"
            >
              CK Ecosystem
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Right Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={DOWNLOAD_LINKS.ANDROID_APK}
              download="ck-pulse-manager.apk"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 transition-colors shadow-sm shrink-0 whitespace-nowrap"
              title="Download CK Pulse Android Manager App (.apk)"
            >
              <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
              <span>Android App</span>
            </a>
            <a
              href={DOWNLOAD_LINKS.WINDOWS_EXE}
              download="CKPulse.Agent.exe"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl bg-zinc-900/90 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition-colors shrink-0 whitespace-nowrap"
              title="Download CK Pulse Windows Agent (.exe)"
            >
              <Download className="w-3.5 h-3.5 text-pulse-blue" />
              <span>Windows Agent</span>
            </a>
            {user ? (
              <Link
                to={user.role === 'SUPER_ADMIN' ? '/superadmin' : '/admin'}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-lg shadow-pulse-blue/20 transition-all shrink-0 whitespace-nowrap"
              >
                <span>Dashboard</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-lg shadow-pulse-blue/20 transition-all shrink-0 whitespace-nowrap"
              >
                <span>Sign In</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* 3. HERO SECTION */}
      <section className="relative pt-14 sm:pt-20 pb-16 px-4 sm:px-6 max-w-6xl mx-auto text-center hero-glow">
        {/* Hero Eyebrow Pill */}
        <div className="inline-flex flex-wrap items-center justify-center gap-2 px-4 py-1.5 rounded-full bg-zinc-900/90 border border-pulse-blue/30 text-xs text-zinc-200 mb-8 backdrop-blur-xl shadow-[0_0_25px_rgba(79,124,255,0.18)]">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="font-semibold text-white">Gentle Audio Chimes</span>
          <span className="text-zinc-600">•</span>
          <span className="text-pulse-blue font-medium">Instant Telegram &amp; Email Alerts</span>
          <span className="text-zinc-600">•</span>
          <span className="text-emerald-400 font-medium">100% Privacy (Zero Spying)</span>
        </div>

        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight max-w-4xl mx-auto leading-[1.12]">
          <span className="text-shimmer block pb-1">Know when workstations sit idle.</span>
          <span className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3.5 mt-2 sm:mt-3 text-2xl sm:text-5xl lg:text-6xl">
            <span className="text-zinc-400 font-medium">Without</span>
            <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-2xl bg-zinc-900/90 border border-pulse-blue/30 shadow-[0_0_25px_rgba(79,124,255,0.18)] backdrop-blur-md">
              <EyeOff className="w-5 h-5 sm:w-8 sm:h-8 text-pulse-blue shrink-0 animate-pulse" />
              <span
                key={spyIndex}
                className="inline-block text-gradient-blue animate-word-swap font-extrabold"
              >
                {SPY_ALTERNATIVES[spyIndex]}
              </span>
            </span>
          </span>
        </h1>

        <p className="mt-6 text-sm sm:text-base lg:text-lg text-zinc-300 max-w-2xl mx-auto leading-relaxed">
          When an employee steps away from their PC for too long, CK Pulse plays a friendly chime on their speakers, warns their screen, and updates managers automatically.
          <strong className="text-white block mt-1">No cameras. No keyloggers. No creepy screen recordings.</strong>
        </p>

        {/* Hero CTAs */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 relative z-20">
          {user ? (
            <Link
              to={user.role === 'SUPER_ADMIN' ? '/superadmin' : '/admin'}
              className="btn-glow-primary w-full sm:w-auto px-7 py-3.5 text-xs sm:text-sm font-semibold rounded-xl text-white flex items-center justify-center gap-2 shadow-2xl"
            >
              <span>Launch Your Dashboard</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <>
              <Link
                to="/activate"
                className="btn-glow-primary w-full sm:w-auto px-7 py-3.5 text-xs sm:text-sm font-semibold rounded-xl text-white flex items-center justify-center gap-2 shadow-2xl"
              >
                <KeyRound className="w-4 h-4" />
                <span>Activate License Key</span>
              </Link>
              <a
                href={DOWNLOAD_LINKS.ANDROID_APK}
                download="ck-pulse-manager.apk"
                className="w-full sm:w-auto px-6 py-3.5 text-xs sm:text-sm font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-400/40 transition-all flex items-center justify-center gap-2 shadow-xl shadow-emerald-950/40 backdrop-blur-md"
              >
                <Smartphone className="w-4 h-4 text-white" />
                <span>Android App (.apk)</span>
              </a>
              <a
                href={DOWNLOAD_LINKS.WINDOWS_EXE}
                download="CKPulse.Agent.exe"
                className="w-full sm:w-auto px-6 py-3.5 text-xs sm:text-sm font-semibold rounded-xl bg-zinc-900/90 hover:bg-zinc-800 text-white border border-zinc-700/80 hover:border-pulse-blue/50 transition-all flex items-center justify-center gap-2 shadow-xl backdrop-blur-md"
              >
                <Download className="w-4 h-4 text-pulse-blue" />
                <span>Windows Agent (.exe)</span>
              </a>
              <Link
                to="/login"
                className="w-full sm:w-auto px-5 py-3.5 text-xs sm:text-sm font-medium rounded-xl bg-zinc-900/50 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition-all flex items-center justify-center gap-2 backdrop-blur-md"
              >
                <span>Sign In</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}

          <a
            href="https://ck-ecosystem.pages.dev/"
            target="_blank"
            rel="noreferrer"
            className="w-full sm:w-auto px-5 py-3.5 text-xs sm:text-sm font-medium rounded-xl bg-zinc-950/60 hover:bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800/80 transition-all flex items-center justify-center gap-2 backdrop-blur-md"
          >
            <span>Explore CK Ecosystem</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Social Proof Trust Badge */}
        <div className="mt-5 flex items-center justify-center gap-2 text-xs text-zinc-400">
          <div className="flex -space-x-1">
            {['#4F46E5', '#2563EB', '#059669', '#D97706'].map((c, i) => (
              <span key={i} className="w-5 h-5 rounded-full border border-black flex items-center justify-center text-[9px] font-bold text-white shadow-sm" style={{ backgroundColor: c }}>
                ★
              </span>
            ))}
          </div>
          <span>Trusted by 250+ engineering teams &amp; remote offices</span>
          <span className="text-zinc-600 hidden sm:inline">•</span>
          <span className="text-emerald-400 font-medium hidden sm:inline">100% Free Trial</span>
        </div>

        {/* HERO LIVE COMMAND CENTER & WORKSTATION HUD MOCKUP */}
        <div className="mt-14 text-left relative z-20">
          {/* Floating Badges Framing the Command Center */}
          <div className="hidden md:flex items-center justify-between px-4 -mb-3.5 relative z-30 pointer-events-none">
            <div className="p-2.5 px-4 rounded-xl glass-pill-chip flex items-center gap-2 border-emerald-500/30 shadow-[0_0_25px_rgba(16,185,129,0.2)] animate-float-slow">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-semibold text-white">Workstation 04 (Design Lab)</span>
              <span className="text-[10px] text-emerald-300 font-mono font-bold bg-emerald-500/20 px-1.5 py-0.5 rounded">ONLINE</span>
            </div>
            <div className="p-2.5 px-4 rounded-xl glass-pill-chip flex items-center gap-2 border-pulse-blue/40 shadow-[0_0_25px_rgba(79,124,255,0.2)] animate-float-reverse">
              <ShieldCheck className="w-4 h-4 text-pulse-blue" />
              <span className="text-xs font-semibold text-white">Zero-Spy Guarantee</span>
              <span className="text-[10px] text-pulse-blue font-mono font-bold bg-pulse-blue/20 px-1.5 py-0.5 rounded">100% PRIVATE</span>
            </div>
          </div>
          <div className="card-glow-border rounded-3xl bg-[#0B0C10]/95 border border-zinc-800/90 shadow-[0_25px_80px_-15px_rgba(0,0,0,0.9)] overflow-hidden">
            {/* Window Titlebar */}
            <div className="px-5 py-3.5 bg-zinc-950/90 border-b border-zinc-800/80 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#FF5F56] border border-[#E0443E]/50 inline-block" />
                <span className="w-3 h-3 rounded-full bg-[#FFBD2E] border border-[#DEA123]/50 inline-block" />
                <span className="w-3 h-3 rounded-full bg-[#27C93F] border border-[#1AAB29]/50 inline-block" />
                <span className="ml-3 text-xs font-mono font-medium text-zinc-400 flex items-center gap-2">
                  <Terminal className="w-3.5 h-3.5 text-pulse-blue" />
                  <span>CKPulse.Agent.exe</span>
                  <span className="text-zinc-600">—</span>
                  <span className="text-zinc-300">Workstation 04 (Windows 11 x64)</span>
                </span>
              </div>

              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Cloudflare APAC Edge: Active
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono text-zinc-400 bg-zinc-900 border border-zinc-800">
                  <ShieldCheck className="w-3 h-3 text-pulse-blue" />
                  Zero-Keylog Guard
                </span>
              </div>
            </div>

            {/* Interactive Simulator Controls Ribbon */}
            <div className="px-6 py-4 bg-zinc-950/40 border-b border-zinc-800/80 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="text-xs text-zinc-400 font-medium">Try It Live:</span>
                <button
                  type="button"
                  onClick={handleSimulateInactivity}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 transition-all flex items-center gap-1.5 shadow-sm"
                >
                  <Play className="w-3.5 h-3.5" />
                  <span>Simulate Away from PC</span>
                </button>
                <button
                  type="button"
                  onClick={handleSimulateActivity}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20 transition-all flex items-center gap-1.5 shadow-sm"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Simulate Back at PC</span>
                </button>
              </div>

              {/* Threshold Switcher Chips */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-zinc-400 font-medium">Alert Triggers After:</span>
                {[
                  { label: '30s', val: 30 },
                  { label: '1 min (60s)', val: 60 },
                  { label: '2 mins', val: 120 },
                  { label: '3 mins', val: 180 },
                  { label: '5 mins', val: 300 },
                ].map((item) => (
                  <button
                    key={item.val}
                    type="button"
                    onClick={() => {
                      setThresholdSeconds(item.val);
                      if (idleSeconds >= item.val) {
                        setSimStatus('INACTIVE');
                        setShowToast(true);
                      } else {
                        setSimStatus('ACTIVE');
                        setShowToast(false);
                      }
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-all ${
                      thresholdSeconds === item.val
                        ? 'bg-pulse-blue text-black font-bold shadow-[0_0_12px_rgba(59,130,246,0.6)] scale-105'
                        : 'bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Sound Tester with Animated Soundwaves */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={playAlertSound}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-lg border transition-all flex items-center gap-2 ${
                    soundPlaying
                      ? 'bg-pulse-blue text-black border-pulse-blue shadow-lg shadow-pulse-blue/40 scale-105'
                      : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border-zinc-700'
                  }`}
                >
                  <Volume2 className="w-4 h-4 text-amber-400" />
                  <span>{soundPlaying ? 'Playing Chime...' : 'Listen to Audio Chime'}</span>
                  <div className="flex items-end gap-0.5 h-4 ml-1">
                    <span className={`w-1 rounded-full ${soundPlaying ? 'bg-black soundwave-bar-1' : 'bg-zinc-600 h-1.5'}`} />
                    <span className={`w-1 rounded-full ${soundPlaying ? 'bg-black soundwave-bar-2' : 'bg-zinc-600 h-2.5'}`} />
                    <span className={`w-1 rounded-full ${soundPlaying ? 'bg-black soundwave-bar-3' : 'bg-zinc-600 h-1.5'}`} />
                    <span className={`w-1 rounded-full ${soundPlaying ? 'bg-black soundwave-bar-4' : 'bg-zinc-600 h-3'}`} />
                  </div>
                </button>
              </div>
            </div>

            {/* Simulator Interactive Grid */}
            <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              {/* Left: Workstation State & Idle Timer */}
              <div className="spotlight-card p-5 rounded-2xl space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-zinc-400" />
                    <span className="text-xs font-semibold text-white">Workstation 04</span>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border flex items-center gap-1.5 ${
                      simStatus === 'ACTIVE'
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                        : 'bg-amber-500/10 text-amber-400 border-amber-500/30 animate-pulse'
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        simStatus === 'ACTIVE' ? 'bg-emerald-400' : 'bg-amber-400'
                      }`}
                    />
                    {simStatus === 'ACTIVE' ? 'Working at Desk' : 'Away from Desk'}
                  </span>
                </div>

                <div className="space-y-0.5">
                  <div className="text-[11px] text-zinc-500">Assigned Team Member</div>
                  <div className="text-sm font-semibold text-white">Peter Mrema (Design Team)</div>
                </div>

                {/* Circular Idle Radar & Counter */}
                <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800/80 flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <span className="text-[11px] text-zinc-400 block font-medium">Stillness Counter</span>
                    <span
                      className={`font-mono font-bold text-3xl tracking-tight ${
                        idleSeconds >= thresholdSeconds ? 'text-amber-400' : 'text-white'
                      }`}
                    >
                      {formatSeconds(idleSeconds)}
                    </span>
                    <div className="text-[11px] text-zinc-400 font-medium">
                      Alert setting: {getThresholdLabel(thresholdSeconds)}
                    </div>
                  </div>

                  <div className="relative flex items-center justify-center">
                    <div
                      className={`w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all ${
                        idleSeconds >= thresholdSeconds
                          ? 'border-amber-400 text-amber-400 radar-pulse bg-amber-500/15'
                          : 'border-pulse-blue text-pulse-blue bg-pulse-blue/10'
                      }`}
                    >
                      <Activity className="w-6 h-6" />
                    </div>
                  </div>
                </div>

                {/* Progress bar towards threshold */}
                <div className="space-y-1.5">
                  <div className="w-full bg-zinc-900 h-2.5 rounded-full overflow-hidden border border-zinc-800">
                    <div
                      className={`h-full transition-all duration-500 ${
                        idleSeconds >= thresholdSeconds
                          ? 'bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.7)]'
                          : 'bg-pulse-blue shadow-[0_0_12px_rgba(59,130,246,0.6)]'
                      }`}
                      style={{ width: `${Math.min(100, (idleSeconds / thresholdSeconds) * 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[11px] font-medium">
                    <span className="text-zinc-500">{Math.min(100, Math.round((idleSeconds / thresholdSeconds) * 100))}% towards alert</span>
                    <span className={idleSeconds >= thresholdSeconds ? 'text-amber-400 font-bold' : 'text-zinc-400'}>
                      {idleSeconds >= thresholdSeconds ? 'ALERT TRIGGERED' : 'Moving Normally'}
                    </span>
                  </div>
                </div>

                <div className="text-[11px] text-zinc-400 flex items-center justify-between pt-2 border-t border-zinc-900">
                  <span>How it detects:</span>
                  <span className="text-white font-medium">Mouse &amp; Keyboard Activity</span>
                </div>
              </div>

              {/* Middle: Simulated Windows 11 Desktop Notification */}
              <div className="spotlight-card p-5 rounded-2xl space-y-3 relative">
                <div className="flex items-center justify-between text-xs text-zinc-400">
                  <span className="font-semibold text-white">What Peter Sees on His Screen</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-900 text-zinc-400 border border-zinc-800">
                    On-Screen Banner
                  </span>
                </div>

                <div
                  className={`p-4 rounded-xl border transition-all duration-300 shadow-2xl ${
                    showToast
                      ? 'bg-zinc-900 border-amber-500/50 translate-y-0 opacity-100 shadow-amber-500/10'
                      : 'bg-zinc-900/30 border-zinc-800/50 opacity-40'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <img src="/pulse-logo.png" alt="CK Pulse" className="w-8 h-8 object-contain shrink-0 drop-shadow" />
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold text-white">CK Pulse — Inactivity Reminder</span>
                        <span className="text-[10px] text-zinc-500">Just now</span>
                      </div>
                      <p className="text-xs text-zinc-300 leading-relaxed">
                        Taking a quick break? No movement detected on this workstation for <strong>{getThresholdLabel(thresholdSeconds)}</strong>.
                      </p>
                      <div className="text-[11px] text-amber-300 pt-1 flex items-center gap-1.5 font-medium">
                        <Volume2 className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        <span>Gentle audio chime played on PC speakers</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-zinc-900/40 border border-zinc-800/50 text-xs text-zinc-400 flex items-start gap-2.5 leading-relaxed">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>
                    <strong>Respectful &amp; Transparent:</strong> The employee is given a friendly reminder first. The moment they touch their mouse, the alert disappears instantly.
                  </span>
                </div>
              </div>

              {/* Right: What Managers & Team Leads Receive */}
              <div className="spotlight-card p-5 rounded-2xl space-y-3 font-sans text-xs">
                <div className="flex items-center justify-between text-zinc-400">
                  <span className="font-semibold text-white">What Managers Receive</span>
                  <span className="text-[10px] text-emerald-400 font-mono px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Instant Dispatch
                  </span>
                </div>

                {simStatus === 'INACTIVE' ? (
                  <div className="p-4 rounded-xl bg-amber-950/20 border border-amber-800/40 space-y-3">
                    <div className="text-amber-300 font-semibold flex items-center gap-1.5">
                      <BellRing className="w-4 h-4 text-amber-400" />
                      <span>Inactivity Alert Dispatched</span>
                    </div>

                    <ul className="space-y-2 text-xs text-zinc-300">
                      <li className="flex items-start gap-2">
                        <span className="text-pulse-blue font-bold">1.</span>
                        <span><strong>Telegram:</strong> Instant bot message sent to team channel: <em>&quot;Peter Mrema away for {getThresholdLabel(thresholdSeconds)}&quot;</em></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-pulse-blue font-bold">2.</span>
                        <span><strong>Dashboard:</strong> Workstation 04 flagged as Idle with exact timestamp.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-pulse-blue font-bold">3.</span>
                        <span><strong>Email Digest:</strong> Logged into daily attendance and activity report.</span>
                      </li>
                    </ul>

                    <div className="pt-1 text-[11px] text-emerald-400 flex items-center gap-1.5 border-t border-amber-900/40">
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                      <span>Zero keystrokes or screens recorded — 100% private.</span>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 space-y-3">
                    <div className="text-emerald-400 font-semibold flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      <span>Workstation Active &amp; Productive</span>
                    </div>

                    <p className="text-xs text-zinc-300 leading-relaxed">
                      Peter is currently moving the mouse and working normally. The idle timer automatically stays reset to 0.
                    </p>

                    <div className="p-2.5 rounded-lg bg-black/40 border border-zinc-800 text-[11px] text-zinc-400 flex items-center gap-2">
                      <Zap className="w-3.5 h-3.5 text-pulse-blue shrink-0" />
                      <span>No annoying false alarms. Alerts only trigger when stillness exceeds your chosen threshold.</span>
                    </div>
                  </div>
                )}

                <div className="p-2.5 rounded-xl bg-zinc-900/30 border border-zinc-800/40 text-[11px] text-zinc-500 flex items-center gap-2">
                  <span>Channels supported:</span>
                  <span className="text-zinc-300 font-medium">Telegram Bot • Email • Custom Webhooks</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Metrics Badges */}
        <div className="mt-14 pt-10 border-t border-zinc-900/90 grid grid-cols-2 lg:grid-cols-4 gap-4 text-left relative z-20">
          <div className="spotlight-card p-5 rounded-3xl">
            <div className="text-2xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300 tracking-tight">0% Spying</div>
            <div className="text-xs font-semibold text-emerald-400 mt-1.5 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Zero Keylogs or Cameras</span>
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">Protects employee dignity &amp; legal privacy</div>
          </div>
          <div className="spotlight-card p-5 rounded-3xl">
            <div className="text-2xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-pulse-blue to-indigo-400 tracking-tight">&lt; 1.2s</div>
            <div className="text-xs font-semibold text-pulse-blue mt-1.5 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" />
              <span>Instant Alert Delivery</span>
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">Direct push to Telegram &amp; Manager Email</div>
          </div>
          <div className="spotlight-card p-5 rounded-3xl">
            <div className="text-2xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 tracking-tight">12 MB</div>
            <div className="text-xs font-semibold text-purple-400 mt-1.5 flex items-center gap-1.5">
              <Cpu className="w-3.5 h-3.5" />
              <span>Ultra-Light Background App</span>
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">Zero CPU drain, zero workstation slowdown</div>
          </div>
          <div className="spotlight-card p-5 rounded-3xl">
            <div className="text-2xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400 tracking-tight">Real-Time</div>
            <div className="text-xs font-semibold text-amber-400 mt-1.5 flex items-center gap-1.5">
              <Volume2 className="w-3.5 h-3.5" />
              <span>Audio Chime Reminder</span>
            </div>
            <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">Gentle bell sound alerts employee first</div>
          </div>
        </div>
      </section>

      {/* 4. PRIVACY-FIRST ARCHITECTURE VS INVASIVE SURVEILLANCE */}
      <section className="py-16 px-4 sm:px-6 max-w-6xl mx-auto border-t border-zinc-900">
        <div className="text-center mb-12">
          <span className="text-[10px] font-mono uppercase tracking-widest text-pulse-blue font-semibold">
            Privacy & Trust Architecture
          </span>
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-white mt-1">
            Why Modern Enterprises Reject Spyware
          </h2>
          <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-2xl mx-auto">
            Traditional employee tracking relies on invasive keyloggers and stealth screen recording that damage company culture. CK Pulse delivers accountability with transparent dignity.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Legacy Spyware Card */}
          <div className="spotlight-card p-7 rounded-3xl border-red-500/30 shadow-[0_0_35px_rgba(239,68,68,0.12)] space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-red-500/10 text-red-400 border border-red-500/20">
                <XCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">Legacy Employee Spyware</h3>
                <span className="text-xs text-zinc-500">Teramind, ActivTrak, TimeDoctor clones</span>
              </div>
            </div>

            <ul className="space-y-3 pt-2 text-xs text-zinc-400">
              <li className="flex items-start gap-2.5">
                <span className="text-red-400 font-bold mt-0.5">✕</span>
                <span>Stealth background keyloggers capturing passwords and private messages.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-red-400 font-bold mt-0.5">✕</span>
                <span>Invasive webcam captures and continuous desktop screenshotting.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-red-400 font-bold mt-0.5">✕</span>
                <span>Continuous 24/7 network spam sending megabytes of telemetry every minute.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-red-400 font-bold mt-0.5">✕</span>
                <span>Destroys team morale, causes employee churn, and creates GDPR compliance risk.</span>
              </li>
            </ul>
          </div>

          {/* CK Pulse Privacy Architecture */}
          <div className="spotlight-card p-7 rounded-3xl border-pulse-blue/40 shadow-[0_0_40px_rgba(79,124,255,0.18)] bg-gradient-to-b from-blue-950/30 via-zinc-950 to-[#0A0D18] space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-pulse-blue/20 text-pulse-blue border border-pulse-blue/30 shadow-[0_0_20px_rgba(79,124,255,0.3)]">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">CK Pulse Privacy Architecture</h3>
                <span className="text-xs text-pulse-blue font-medium">Respectful, transparent workstation monitoring</span>
              </div>
            </div>

            <ul className="space-y-3 pt-2 text-xs text-zinc-300">
              <li className="flex items-start gap-2.5">
                <span className="text-emerald-400 font-bold mt-0.5">✓</span>
                <span><strong>0% Keystrokes Captured:</strong> We only check if mouse or keyboard is moving—never what you type, read, or browse.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-emerald-400 font-bold mt-0.5">✓</span>
                <span><strong>Gentle Audio Reminder:</strong> A pleasant chime and polite on-screen banner gives the employee the chance to resume work first.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-emerald-400 font-bold mt-0.5">✓</span>
                <span><strong>Zero Network Spam:</strong> Silent, ultra-light background app that never eats bandwidth or drains laptop batteries.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-emerald-400 font-bold mt-0.5">✓</span>
                <span><strong>Simple &amp; Secure:</strong> 1-click installer and safe company keys. No complex IT infrastructure required.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 4.8. WINDOWS DESKTOP AGENT DOWNLOAD & DEPLOYMENT CENTER */}
      <section className="py-16 px-4 sm:px-6 max-w-6xl mx-auto border-t border-zinc-900">
        <div className="glass-card rounded-3xl p-8 sm:p-10 border border-zinc-800/90 relative overflow-hidden bg-gradient-to-b from-zinc-950 via-[#0E0F14] to-zinc-950">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="space-y-4 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pulse-blue/10 border border-pulse-blue/20 text-pulse-blue text-xs font-semibold">
                <HardDrive className="w-3.5 h-3.5" />
                <span>Lightweight Windows Agent (v1.0.0)</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Get Started in Less Than 60 Seconds
              </h2>

              <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed">
                Download the lightweight Windows app. It sits quietly near the clock in your system tray, plays a gentle chime when employees step away, and updates your dashboard automatically.
              </p>

              <div className="flex flex-wrap gap-4 pt-1 text-xs text-zinc-400 font-mono">
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-400" /> Windows 11 &amp; 10 (x64)
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-400" /> Single Portable .EXE
                </span>
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-400" /> Uses almost zero battery &amp; RAM
                </span>
              </div>
            </div>

            <div className="w-full lg:w-auto space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={DOWNLOAD_LINKS.ANDROID_APK}
                  download="ck-pulse-manager.apk"
                  className="px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-xl shadow-emerald-950/40 transition-all"
                >
                  <Smartphone className="w-4 h-4 text-white" />
                  <span>Download Android App (.apk)</span>
                </a>

                <a
                  href={DOWNLOAD_LINKS.WINDOWS_ZIP}
                  download="CKPulse-Agent-v1.0.0-win-x64.zip"
                  className="px-5 py-3.5 rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white font-semibold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-xl shadow-pulse-blue/30 transition-all"
                >
                  <Download className="w-4 h-4" />
                  <span>Windows ZIP Bundle</span>
                </a>

                <a
                  href={DOWNLOAD_LINKS.WINDOWS_EXE}
                  download="CKPulse.Agent.exe"
                  className="px-4 py-3.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white font-medium text-xs sm:text-sm border border-zinc-700 flex items-center justify-center gap-2 transition-all"
                >
                  <HardDrive className="w-4 h-4 text-zinc-400" />
                  <span>Windows .EXE</span>
                </a>
              </div>

              {/* Quick PowerShell command */}
              <div className="p-3 rounded-xl bg-black/60 border border-zinc-800/80 flex items-center justify-between gap-3 text-xs font-mono text-zinc-400">
                <span className="truncate">powershell -c &quot;Invoke-WebRequest https://ck-pulse.pages.dev/downloads/CKPulse.Agent.exe -OutFile CKPulse.exe&quot;</span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText('powershell -c "Invoke-WebRequest https://ck-pulse.pages.dev/downloads/CKPulse.Agent.exe -OutFile CKPulse.exe"');
                    setCopiedCommand(true);
                    setTimeout(() => setCopiedCommand(false), 2000);
                  }}
                  className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors shrink-0"
                  title="Copy command"
                >
                  {copiedCommand ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4.5. HOW IT WORKS IN 3 SIMPLE STEPS */}
      <section className="py-16 px-4 sm:px-6 max-w-6xl mx-auto border-t border-zinc-900">
        <div className="text-center mb-10">
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 font-semibold">
            Simple &amp; Automated
          </span>
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-white mt-1">
            How It Works in 3 Simple Steps
          </h2>
          <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-2xl mx-auto">
            From desk stillness to manager notification: completely hands-off and automated.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Step 1 */}
          <div className="p-6 rounded-3xl bg-zinc-950/80 border border-zinc-800 relative group hover:border-pulse-blue/50 transition-all space-y-3">
            <div className="w-10 h-10 rounded-2xl bg-pulse-blue/10 border border-pulse-blue/30 text-pulse-blue flex items-center justify-center font-bold text-sm">
              01
            </div>
            <h3 className="text-base font-semibold text-white">Choose Your Alert Time</h3>
            <p className="text-xs text-zinc-300 leading-relaxed">
              Decide how many minutes of stillness trigger an alert (such as 1 minute, 3 minutes, or 5 minutes). Your rule updates across all company computers automatically.
            </p>
            <div className="p-2.5 rounded-xl bg-zinc-900/60 border border-zinc-800/80 text-[11px] text-zinc-400 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-pulse-blue shrink-0" />
              <span>Set once in your web dashboard</span>
            </div>
          </div>

          {/* Step 2 */}
          <div className="p-6 rounded-3xl bg-zinc-950/80 border border-zinc-800 relative group hover:border-pulse-blue/50 transition-all space-y-3">
            <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center font-bold text-sm">
              02
            </div>
            <h3 className="text-base font-semibold text-white">Friendly Employee Reminder</h3>
            <p className="text-xs text-zinc-300 leading-relaxed">
              If someone steps away from their PC, a gentle chime sounds and an on-screen banner appears. The employee can simply touch their mouse to dismiss it instantly.
            </p>
            <div className="p-2.5 rounded-xl bg-zinc-900/60 border border-zinc-800/80 text-[11px] text-zinc-400 flex items-center gap-2">
              <Volume2 className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span>Audio chime + desktop banner</span>
            </div>
          </div>

          {/* Step 3 */}
          <div className="p-6 rounded-3xl bg-zinc-950/80 border border-zinc-800 relative group hover:border-pulse-blue/50 transition-all space-y-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center font-bold text-sm">
              03
            </div>
            <h3 className="text-base font-semibold text-white">Managers Get Notified</h3>
            <p className="text-xs text-zinc-300 leading-relaxed">
              If the workstation stays idle, managers receive a real-time message via Telegram or Email with the employee&apos;s name and total idle duration.
            </p>
            <div className="p-2.5 rounded-xl bg-zinc-900/60 border border-zinc-800/80 text-[11px] text-zinc-400 flex items-center gap-2">
              <Send className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span>Instant Telegram bot &amp; Email alerts</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CK ECOSYSTEM INTEGRATION SECTION */}
      <section className="py-16 px-4 sm:px-6 max-w-6xl mx-auto border-t border-zinc-900">
        <div className="glass-card rounded-3xl p-8 sm:p-12 relative overflow-hidden bg-gradient-to-b from-blue-950/20 to-zinc-950">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-4 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pulse-blue/10 border border-pulse-blue/20 text-pulse-blue text-xs font-semibold">
                <span>The CK Ecosystem Synergy</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Part of a Unified Digital Platform Suite
              </h3>
              <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
                CK Pulse is designed to operate seamlessly alongside the other digital solutions in the <strong>CK Ecosystem</strong>.
                Together, they empower organizations with centralized administration, unified cryptographic licensing, and enterprise-grade cloud telemetry.
              </p>
              <div className="flex items-center gap-3 pt-2">
                <a
                  href="https://ck-ecosystem.pages.dev/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-xl bg-white text-zinc-900 hover:bg-zinc-200 shadow-xl transition-all"
                >
                  <span>Visit CK Ecosystem Portal</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <Link
                  to="/activate"
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-medium rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border border-zinc-700 transition-all"
                >
                  <span>Activate License</span>
                </Link>
              </div>
            </div>

            <div className="w-full lg:w-96 p-6 rounded-2xl bg-zinc-950/90 border border-zinc-800 space-y-3">
              <div className="text-xs font-semibold text-white flex items-center justify-between border-b border-zinc-800 pb-2">
                <span>Ecosystem Topology</span>
                <span className="text-[10px] text-emerald-400 font-mono">100% Operational</span>
              </div>
              <div className="space-y-2 text-xs">
                <a
                  href="https://ck-ecosystem.pages.dev/"
                  target="_blank"
                  rel="noreferrer"
                  className="p-2.5 rounded-xl bg-zinc-900/60 border border-zinc-800 hover:border-pulse-blue/40 transition-colors flex items-center justify-between group block"
                >
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-pulse-blue" />
                    <div>
                      <div className="font-medium text-white group-hover:text-pulse-blue transition-colors">
                        CK Ecosystem Core
                      </div>
                      <div className="text-[10px] text-zinc-500 font-mono">ck-ecosystem.pages.dev</div>
                    </div>
                  </div>
                  <ExternalLink className="w-3 h-3 text-zinc-500 group-hover:text-pulse-blue" />
                </a>

                <div className="p-2.5 rounded-xl bg-pulse-blue/10 border border-pulse-blue/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-pulse-blue" />
                    <div>
                      <div className="font-semibold text-white">CK Pulse (Current)</div>
                      <div className="text-[10px] text-pulse-blue font-mono">Workstation Observability & Alerting</div>
                    </div>
                  </div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-pulse-blue text-white font-bold">ACTIVE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. CORE PILLARS (BENTO GRID) */}
      <section id="pillars" className="py-16 px-4 sm:px-6 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-[10px] font-mono uppercase tracking-widest text-pulse-blue font-semibold">
            Architectural Excellence
          </span>
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-white mt-1">
            Engineered for Privacy, Scalability & Speed
          </h2>
          <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-xl mx-auto">
            A state-of-the-art software suite built with modern protocols and zero compromise on security.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Zero Keylogging */}
          <div className="glass-card rounded-2xl p-6 space-y-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 w-fit">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-white tracking-tight">Zero-Keylogging Privacy Guarantee</h3>
            <p className="text-xs text-zinc-400 leading-relaxed">
              We calculate idle duration strictly using native Win32 tick differentials. No keystroke recording, no screen capture, and no private data harvesting.
            </p>
          </div>

          {/* Card 2: Event-Driven Cloudflare Edge */}
          <div className="glass-card rounded-2xl p-6 space-y-3">
            <div className="p-2.5 rounded-xl bg-pulse-blue/10 text-pulse-blue border border-pulse-blue/20 w-fit">
              <Zap className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-white tracking-tight">Event-Driven Edge Efficiency</h3>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Zero network calls while the user is typing or active. Fired only the exact second the inactivity limit is crossed, cutting server requests by over 99%.
            </p>
          </div>

          {/* Card 3: Multi-Channel Alerting */}
          <div className="glass-card rounded-2xl p-6 space-y-3">
            <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20 w-fit">
              <BellRing className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-white tracking-tight">Multi-Channel Instant Alerting</h3>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Local workstation audio & Windows toast alerts, paired with real-time cloud dispatch to Telegram bots, dark-mode emails, and signed webhooks.
            </p>
          </div>

          {/* Card 4: Cryptographic Licensing */}
          <div className="glass-card rounded-2xl p-6 space-y-3">
            <div className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 w-fit">
              <KeyRound className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-white tracking-tight">Cryptographic Licensing</h3>
            <p className="text-xs text-zinc-400 leading-relaxed">
              CSPRNG-generated activation keys stored as SHA-256 hashes in Cloudflare D1. Zero-trust single activation preventing unauthorized reuse.
            </p>
          </div>
        </div>
      </section>

      {/* 7. PRIVACY GUARANTEE: SPYWARE VS CK PULSE */}
      <section id="privacy" className="py-16 px-4 sm:px-6 max-w-6xl mx-auto border-t border-zinc-900">
        <div className="text-center mb-10">
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 font-semibold">
            Ethical Observability
          </span>
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-white mt-1">
            Built to Build Trust, Not Breach It
          </h2>
          <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-xl mx-auto">
            See how CK Pulse differs fundamentally from intrusive employee surveillance software.
          </p>
        </div>

        <div className="pulse-card rounded-2xl overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#141416] text-zinc-400 uppercase tracking-wider font-medium border-b border-zinc-800">
                <tr>
                  <th className="py-3.5 px-5">Capability / Protocol</th>
                  <th className="py-3.5 px-5 text-rose-400">Traditional Invasive Spyware</th>
                  <th className="py-3.5 px-5 text-emerald-400 bg-emerald-950/20 border-l border-emerald-800/40">
                    CK Pulse Ethical Standard
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/80 text-zinc-300">
                <tr>
                  <td className="py-3.5 px-5 font-semibold text-white">Keystroke Recording</td>
                  <td className="py-3.5 px-5 text-rose-400/90 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
                    <span>Captures passwords, personal chats, and emails</span>
                  </td>
                  <td className="py-3.5 px-5 text-emerald-300 bg-emerald-950/10 border-l border-emerald-800/40">
                    <div className="flex items-center gap-2 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>ABSOLUTELY ZERO keystroke capture</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="py-3.5 px-5 font-semibold text-white">Screen & Webcam Grabs</td>
                  <td className="py-3.5 px-5 text-rose-400/90 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
                    <span>Secretly takes screenshots and records video</span>
                  </td>
                  <td className="py-3.5 px-5 text-emerald-300 bg-emerald-950/10 border-l border-emerald-800/40">
                    <div className="flex items-center gap-2 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>ZERO video recording or screen captures</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="py-3.5 px-5 font-semibold text-white">Detection Mechanism</td>
                  <td className="py-3.5 px-5 text-zinc-400">
                    Low-level invasive OS root hooks, memory scraping
                  </td>
                  <td className="py-3.5 px-5 text-emerald-300 bg-emerald-950/10 border-l border-emerald-800/40 font-medium">
                    Native Win32 <code className="text-emerald-400 font-mono">GetLastInputInfo</code> (idle tick timer only)
                  </td>
                </tr>
                <tr>
                  <td className="py-3.5 px-5 font-semibold text-white">Employee Transparency</td>
                  <td className="py-3.5 px-5 text-rose-400/90 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-rose-400 shrink-0" />
                    <span>Runs stealthily without employee knowledge</span>
                  </td>
                  <td className="py-3.5 px-5 text-emerald-300 bg-emerald-950/10 border-l border-emerald-800/40">
                    <div className="flex items-center gap-2 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>System tray icon, audible chime, and desktop toast</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="py-3.5 px-5 font-semibold text-white">Network Traffic</td>
                  <td className="py-3.5 px-5 text-zinc-400">
                    Continuous heavy uploading of screens and keystrokes
                  </td>
                  <td className="py-3.5 px-5 text-emerald-300 bg-emerald-950/10 border-l border-emerald-800/40 font-medium">
                    Lightweight event-driven JSON payloads (&lt; 10 calls/day)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 8. MULTI-CHANNEL NOTIFICATION CENTER SHOWCASE */}
      <section id="notifications" className="py-16 px-4 sm:px-6 max-w-6xl mx-auto border-t border-zinc-900">
        <div className="text-center mb-10">
          <span className="text-[10px] font-mono uppercase tracking-widest text-pulse-blue font-semibold">
            Instant Alert Channels
          </span>
          <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-white mt-1">
            Stay Notified on Any Platform
          </h2>
          <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-xl mx-auto">
            Configure automated dispatches to your team via Telegram bots, responsive emails, or webhooks.
          </p>
        </div>

        <div className="glass-card rounded-3xl p-6 sm:p-8">
          {/* Channel Selector Tabs */}
          <div className="flex items-center justify-center gap-2 mb-6 border-b border-zinc-800 pb-4">
            <button
              onClick={() => setActiveChannelTab('TELEGRAM')}
              className={`px-4 py-2 rounded-xl text-xs font-medium flex items-center gap-2 transition-all ${
                activeChannelTab === 'TELEGRAM'
                  ? 'bg-pulse-blue text-white shadow-lg shadow-pulse-blue/20'
                  : 'text-zinc-400 hover:text-white bg-zinc-900/60'
              }`}
            >
              <Send className="w-3.5 h-3.5" />
              <span>Telegram Bot</span>
            </button>
            <button
              onClick={() => setActiveChannelTab('EMAIL')}
              className={`px-4 py-2 rounded-xl text-xs font-medium flex items-center gap-2 transition-all ${
                activeChannelTab === 'EMAIL'
                  ? 'bg-pulse-blue text-white shadow-lg shadow-pulse-blue/20'
                  : 'text-zinc-400 hover:text-white bg-zinc-900/60'
              }`}
            >
              <Mail className="w-3.5 h-3.5" />
              <span>HTML Email</span>
            </button>
            <button
              onClick={() => setActiveChannelTab('WEBHOOK')}
              className={`px-4 py-2 rounded-xl text-xs font-medium flex items-center gap-2 transition-all ${
                activeChannelTab === 'WEBHOOK'
                  ? 'bg-pulse-blue text-white shadow-lg shadow-pulse-blue/20'
                  : 'text-zinc-400 hover:text-white bg-zinc-900/60'
              }`}
            >
              <Code2 className="w-3.5 h-3.5" />
              <span>Signed Webhook</span>
            </button>
          </div>

          {/* Tab Previews */}
          <div className="max-w-2xl mx-auto">
            {activeChannelTab === 'TELEGRAM' && (
              <div className="bg-[#182533] p-5 rounded-2xl border border-blue-900/40 text-xs shadow-2xl space-y-3">
                <div className="flex items-center gap-2 text-zinc-300 font-medium">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-[10px] font-bold text-white">
                    CK
                  </div>
                  <div>
                    <span className="font-semibold text-white">CK Pulse Sentinel Bot</span>
                    <span className="text-[10px] text-zinc-400 ml-2">bot</span>
                  </div>
                </div>
                <div className="bg-[#202F3F] p-4 rounded-xl text-zinc-200 space-y-2 border border-blue-800/30">
                  <div className="font-bold text-amber-400 text-sm flex items-center gap-1.5">
                    <span>⚠️ Workstation Inactivity Detected</span>
                  </div>
                  <div className="text-[11px] text-zinc-300 space-y-1 font-mono">
                    <div><strong>Tenant:</strong> Acme Engineering Ltd</div>
                    <div><strong>Workstation:</strong> WS-CAD-04 (dev_01J_CAD)</div>
                    <div><strong>Employee:</strong> Peter Mrema</div>
                    <div><strong>Duration:</strong> 3 minutes 42 seconds</div>
                    <div><strong>Status:</strong> Active Inactivity</div>
                  </div>
                  <div className="pt-2 text-[10px] text-zinc-400 flex items-center justify-between border-t border-zinc-700/60">
                    <span>10:42 AM</span>
                    <span className="text-blue-300">View in Dashboard →</span>
                  </div>
                </div>
              </div>
            )}

            {activeChannelTab === 'EMAIL' && (
              <div className="bg-[#121212] p-6 rounded-2xl border border-zinc-800 text-xs space-y-4 shadow-2xl">
                <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
                  <div className="flex items-center gap-2.5">
                    <img src="/pulse-logo.png" alt="CK Pulse" className="w-6 h-6 object-contain" />
                    <span className="font-semibold text-white">CK Pulse Incident Notification</span>
                  </div>
                  <span className="text-[10px] text-zinc-500 font-mono">alert@ck-pulse.pages.dev</span>
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-white">Inactivity Alert: WS-CAD-04</div>
                  <p className="text-zinc-400 text-xs leading-relaxed">
                    Workstation <strong>WS-CAD-04</strong> assigned to <strong>Peter Mrema</strong> has exceeded the configured inactivity threshold of 180 seconds.
                  </p>
                  <div className="p-3 rounded-xl bg-zinc-900 border border-zinc-800 text-xs grid grid-cols-2 gap-2">
                    <div><span className="text-zinc-500">Threshold:</span> 3m 00s</div>
                    <div><span className="text-zinc-500">Current Idle:</span> 3m 42s</div>
                  </div>
                </div>
                <div className="pt-2">
                  <button className="px-4 py-2 rounded-xl bg-pulse-blue text-white text-xs font-medium">
                    Open Live Monitor
                  </button>
                </div>
              </div>
            )}

            {activeChannelTab === 'WEBHOOK' && (
              <div className="bg-black/90 p-5 rounded-2xl border border-zinc-800 text-xs font-mono text-zinc-300 space-y-2 shadow-2xl">
                <div className="text-zinc-500 flex items-center justify-between">
                  <span>POST https://api.yourcompany.com/webhooks/ck-pulse</span>
                  <span className="text-emerald-400">HMAC-SHA256 Signed</span>
                </div>
                <pre className="text-[11px] text-zinc-400 overflow-x-auto p-3 bg-zinc-950 rounded-xl border border-zinc-900">
{`{
  "event": "INACTIVITY_THRESHOLD_CROSSED",
  "timestamp": "2026-09-21T04:30:00.000Z",
  "organizationId": "org_acme_corp",
  "data": {
    "deviceId": "dev_01J_CAD",
    "hostname": "WS-CAD-04",
    "employee": "Peter Mrema",
    "durationSeconds": 222,
    "thresholdSeconds": 180,
    "status": "INACTIVE"
  },
  "signature": "sha256=d87f9b876a..."
}`}
                </pre>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 9. PRICING & LICENSING PLANS (OPTION 2: PER-WORKSTATION SEAT MODEL) */}
      <section id="pricing" className="py-20 px-4 sm:px-6 max-w-7xl mx-auto border-t border-zinc-900 relative">
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-96 h-96 bg-pulse-blue/5 rounded-full blur-3xl pointer-events-none" />

        <div className="text-center mb-10 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pulse-blue/10 border border-pulse-blue/25 text-pulse-blue text-xs font-mono mb-3">
            <Sparkles className="w-3.5 h-3.5" />
            <span>OPTION 2 • PER-WORKSTATION PRICING</span>
          </div>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
            Pay Only For Active Workstations
          </h2>
          <p className="text-xs sm:text-sm text-zinc-400 mt-3 max-w-2xl mx-auto leading-relaxed">
            Every plan includes our native Windows background agent, audio chime alerts, and live dashboard.
            Start with a 7-day free trial on up to 5 PCs. No credit card required.
          </p>

          {/* Pricing Controls Toolbar (Billing Cycle + Currency Switcher) */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            {/* Billing Cycle Toggle */}
            <div className="inline-flex items-center p-1 rounded-xl bg-zinc-900 border border-zinc-800 shadow-inner">
              <button
                type="button"
                onClick={() => setPricingBilling('MONTHLY')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  pricingBilling === 'MONTHLY'
                    ? 'bg-zinc-800 text-white shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Monthly Billing
              </button>
              <button
                type="button"
                onClick={() => setPricingBilling('ANNUAL')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                  pricingBilling === 'ANNUAL'
                    ? 'bg-pulse-blue text-white shadow-md shadow-pulse-blue/20'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <span>Annual Billing</span>
                <span className="px-1.5 py-0.5 rounded bg-white/20 text-[10px] font-bold uppercase tracking-wider">
                  Save 20%
                </span>
              </button>
            </div>

            {/* Currency Selector */}
            <div className="inline-flex items-center p-1 rounded-xl bg-zinc-900 border border-zinc-800">
              <button
                type="button"
                onClick={() => setPricingCurrency('USD')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold font-mono transition-all ${
                  pricingCurrency === 'USD'
                    ? 'bg-zinc-800 text-white shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                USD ($)
              </button>
              <button
                type="button"
                onClick={() => setPricingCurrency('INR')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold font-mono transition-all ${
                  pricingCurrency === 'INR'
                    ? 'bg-zinc-800 text-white shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                INR (₹)
              </button>
            </div>
          </div>
        </div>

        {/* 4 Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 relative z-10">
          {/* Tier 1: 7-Day Free POC */}
          <div className="glass-card rounded-2xl p-6 flex flex-col justify-between border-zinc-800 hover:border-zinc-700 transition-all">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">
                  Free POC
                </span>
                <span className="text-xs text-zinc-500 font-mono">Up to 5 PCs</span>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white">7-Day Trial</h3>
                <p className="text-[11px] text-zinc-400 mt-1 leading-snug">
                  Full evaluation trial to test Windows background agent and local audio alarms.
                </p>
              </div>

              <div className="py-2 border-y border-zinc-800/80">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-extrabold text-white">
                    {pricingCurrency === 'USD' ? '$0' : '₹0'}
                  </span>
                  <span className="text-xs text-zinc-400 font-medium">/ 7 days</span>
                </div>
                <div className="text-[11px] text-emerald-400 font-medium mt-0.5">
                  No credit card required
                </div>
              </div>

              <ul className="space-y-2.5 text-xs text-zinc-300">
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span><strong>Up to 5 Workstations</strong> included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>Windows Background Agent (.exe)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>Real-time Live Monitor</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>Local Audio Alarm & Desktop Toast</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>7-Day Inactivity Audit Logs</span>
                </li>
              </ul>
            </div>

            <div className="pt-6">
              <Link
                to="/activate"
                className="w-full py-2.5 text-center text-xs font-semibold rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700 transition-colors block shadow-sm"
              >
                Start 7-Day Free Trial
              </Link>
            </div>
          </div>

          {/* Tier 2: Starter */}
          <div className="glass-card rounded-2xl p-6 flex flex-col justify-between border-blue-500/20 hover:border-blue-500/40 transition-all">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 font-semibold">
                  Starter Tier
                </span>
                <span className="text-xs text-zinc-500 font-mono">Up to 15 PCs</span>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white">Starter</h3>
                <p className="text-[11px] text-zinc-400 mt-1 leading-snug">
                  For small offices, design studios, and boutique accounting firms.
                </p>
              </div>

              <div className="py-2 border-y border-zinc-800/80">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-extrabold text-white">
                    {pricingCurrency === 'USD'
                      ? pricingBilling === 'ANNUAL' ? '$2.40' : '$3.00'
                      : pricingBilling === 'ANNUAL' ? '₹199' : '₹249'}
                  </span>
                  <span className="text-xs text-zinc-400 font-medium">/ PC / mo</span>
                </div>
                <div className="text-[11px] text-zinc-400 mt-0.5">
                  {pricingBilling === 'ANNUAL' ? 'Billed annually (Save 20%)' : 'Billed monthly'}
                </div>
              </div>

              <ul className="space-y-2.5 text-xs text-zinc-300">
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                  <span><strong>Up to 15 Workstations</strong> included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                  <span>Instant Telegram & Email Alerts</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                  <span>30-Day Activity History & Trends</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                  <span>Team & Department Grouping</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                  <span>CSV Activity Export</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                  <span>Standard Email Support</span>
                </li>
              </ul>
            </div>

            <div className="pt-6">
              <Link
                to="/activate"
                className="w-full py-2.5 text-center text-xs font-semibold rounded-xl bg-blue-600 hover:bg-blue-500 text-white transition-colors block shadow-md shadow-blue-600/20"
              >
                Choose Starter
              </Link>
            </div>
          </div>

          {/* Tier 3: Pro (Most Popular) */}
          <div className="glass-card rounded-2xl p-6 flex flex-col justify-between border-pulse-blue shadow-xl shadow-pulse-blue/10 relative scale-105 z-20 bg-gradient-to-b from-pulse-blue/10 via-zinc-900 to-zinc-950">
            <div className="absolute -top-3 right-6 px-2.5 py-0.5 rounded-full bg-pulse-blue text-[10px] font-bold uppercase tracking-wider text-white shadow-md shadow-pulse-blue/40">
              Most Popular
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-pulse-blue/20 text-pulse-blue border border-pulse-blue/30 font-semibold">
                  Pro Tier
                </span>
                <span className="text-xs text-zinc-400 font-mono">Up to 50 PCs</span>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white">Pro</h3>
                <p className="text-[11px] text-zinc-300 mt-1 leading-snug">
                  Designed for growing firms, BPOs, and remote engineering studios.
                </p>
              </div>

              <div className="py-2 border-y border-pulse-blue/20">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-extrabold text-white">
                    {pricingCurrency === 'USD'
                      ? pricingBilling === 'ANNUAL' ? '$4.00' : '$5.00'
                      : pricingBilling === 'ANNUAL' ? '₹319' : '₹399'}
                  </span>
                  <span className="text-xs text-zinc-300 font-medium">/ PC / mo</span>
                </div>
                <div className="text-[11px] text-pulse-blue font-medium mt-0.5">
                  {pricingBilling === 'ANNUAL' ? 'Billed annually (Save 20%)' : 'Billed monthly'}
                </div>
              </div>

              <ul className="space-y-2.5 text-xs text-zinc-200">
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-pulse-blue shrink-0 mt-0.5" />
                  <span><strong>Up to 50 Workstations</strong> included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-pulse-blue shrink-0 mt-0.5" />
                  <span><strong>Custom Inactivity Timers</strong> (30s – 60m)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-pulse-blue shrink-0 mt-0.5" />
                  <span><strong>Custom Audio Chime</strong> (Breeze, Bell, Digital)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-pulse-blue shrink-0 mt-0.5" />
                  <span>Signed Outbound Webhooks (Slack/Teams)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-pulse-blue shrink-0 mt-0.5" />
                  <span>90-Day History & Audit Trails</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-pulse-blue shrink-0 mt-0.5" />
                  <span>Priority 4-Hour Response SLA</span>
                </li>
              </ul>
            </div>

            <div className="pt-6">
              <Link
                to="/activate"
                className="w-full py-2.5 text-center text-xs font-semibold rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-lg shadow-pulse-blue/30 transition-all block"
              >
                Choose Pro (50 PCs)
              </Link>
            </div>
          </div>

          {/* Tier 4: Enterprise */}
          <div className="glass-card rounded-2xl p-6 flex flex-col justify-between border-amber-500/20 hover:border-amber-500/40 transition-all">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 font-semibold">
                  Enterprise
                </span>
                <span className="text-xs text-zinc-500 font-mono">50+ / Unlimited</span>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white">Enterprise</h3>
                <p className="text-[11px] text-zinc-400 mt-1 leading-snug">
                  For multi-branch firms, large call centers, and strict compliance.
                </p>
              </div>

              <div className="py-2 border-y border-zinc-800/80">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-extrabold text-white">
                    {pricingCurrency === 'USD'
                      ? pricingBilling === 'ANNUAL' ? '$6.40' : '$8.00'
                      : pricingBilling === 'ANNUAL' ? '₹519' : '₹649'}
                  </span>
                  <span className="text-xs text-zinc-400 font-medium">/ PC / mo</span>
                </div>
                <div className="text-[11px] text-zinc-400 mt-0.5">
                  {pricingBilling === 'ANNUAL' ? 'Billed annually (Save 20%)' : 'Billed monthly'}
                </div>
              </div>

              <ul className="space-y-2.5 text-xs text-zinc-300">
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <span><strong>Unlimited Workstations</strong> Quota</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Dedicated Account Manager</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Custom Sound Chimes & White-labeling</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Enterprise SSO / SAML Integration</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <span>1-Year Audit Retention & Compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <span>99.9% Uptime SLA Guarantee</span>
                </li>
              </ul>
            </div>

            <div className="pt-6">
              <a
                href="mailto:sales@ck-pulse.com?subject=CK%20Pulse%20Enterprise%20Inquiry"
                className="w-full py-2.5 text-center text-xs font-semibold rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700 transition-colors block shadow-sm"
              >
                Contact Enterprise Sales
              </a>
            </div>
          </div>
        </div>

        {/* Interactive Option 2 Workstation Seat Calculator */}
        <div className="mt-12 max-w-3xl mx-auto p-6 rounded-2xl bg-zinc-900/70 border border-zinc-800 backdrop-blur-sm shadow-xl relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-zinc-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-pulse-blue/10 border border-pulse-blue/20 text-pulse-blue">
                <Calculator className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white tracking-tight">Quick Cost Estimator</h4>
                <p className="text-[11px] text-zinc-400">Calculate your exact investment based on monitored PCs</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400">Monitored PCs:</span>
              <span className="px-3 py-1 rounded-lg bg-zinc-950 text-white font-mono text-xs font-bold border border-zinc-800">
                {calculatorSeats} Workstations
              </span>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={calculatorSeats}
                onChange={(e) => setCalculatorSeats(parseInt(e.target.value))}
                className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-pulse-blue"
              />
            </div>

            <div className="flex justify-between text-[10px] text-zinc-500 font-mono">
              <span>5 PCs (Trial/Starter)</span>
              <span>15 PCs (Starter)</span>
              <span>25 PCs</span>
              <span>50 PCs (Pro)</span>
              <span>100 PCs (Enterprise)</span>
            </div>

            {/* Calculated summary pill */}
            <div className="p-4 rounded-xl bg-zinc-950/80 border border-zinc-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold text-white">
                  Estimated Total for {calculatorSeats} Workstations:
                </div>
                <div className="text-[11px] text-zinc-400">
                  {calculatorSeats <= 15
                    ? 'Recommended: Starter Tier ($3.00/PC or $2.40 annual)'
                    : calculatorSeats <= 50
                    ? 'Recommended: Pro Tier ($5.00/PC or $4.00 annual)'
                    : 'Recommended: Enterprise Tier ($8.00/PC or $6.40 annual)'}
                </div>
              </div>

              <div className="text-right">
                <div className="text-2xl font-extrabold text-white font-mono">
                  {(() => {
                    const unitRate =
                      calculatorSeats <= 15
                        ? pricingCurrency === 'USD'
                          ? pricingBilling === 'ANNUAL' ? 2.40 : 3.00
                          : pricingBilling === 'ANNUAL' ? 199 : 249
                        : calculatorSeats <= 50
                        ? pricingCurrency === 'USD'
                          ? pricingBilling === 'ANNUAL' ? 4.00 : 5.00
                          : pricingBilling === 'ANNUAL' ? 319 : 399
                        : pricingCurrency === 'USD'
                        ? pricingBilling === 'ANNUAL' ? 6.40 : 8.00
                        : pricingBilling === 'ANNUAL' ? 519 : 649;

                    const total = Math.round(unitRate * calculatorSeats);
                    return pricingCurrency === 'USD' ? `$${total} / mo` : `₹${total.toLocaleString()} / mo`;
                  })()}
                </div>
                <div className="text-[10px] text-pulse-blue font-medium">
                  {pricingBilling === 'ANNUAL' ? 'Billed annually with 20% savings' : 'Billed monthly'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Native Windows Agent Download Card */}
        <div className="mt-12 max-w-2xl mx-auto p-6 rounded-2xl bg-gradient-to-b from-zinc-900/90 to-zinc-950/95 border border-zinc-800 backdrop-blur-md shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-pulse-blue/10 rounded-full blur-3xl pointer-events-none" />
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 relative z-10">
            <div className="space-y-1.5 text-left">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[11px] font-mono font-semibold uppercase tracking-wider text-pulse-blue">
                  Official Release v1.0.0
                </span>
                <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-[10px] text-zinc-300 font-mono border border-zinc-700">
                  x64
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-bold text-white tracking-tight">
                Native Windows Workstation Agent
              </h3>
              <p className="text-xs text-zinc-400 leading-relaxed max-w-md">
                Ultra-lightweight 210 KB binary running discreetly in the Windows system tray. Pair with any company account in under 30 seconds via one-time pairing code.
              </p>
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-500 font-mono pt-1">
                <span>• Windows 10 / 11</span>
                <span>• &lt; 0.1% CPU</span>
                <span>• Zero Keylogging</span>
              </div>

              {/* Runtime helper notice */}
              <div className="pt-2 text-[11px] text-zinc-400 flex items-center gap-2">
                <span className="text-zinc-500">Prerequisite:</span>
                <span className="text-zinc-300">Microsoft .NET Desktop Runtime 9.0</span>
                <span className="text-zinc-600">•</span>
                <a
                  href="https://aka.ms/dotnet/9.0/windowsdesktop-runtime-win-x64.exe"
                  target="_blank"
                  rel="noreferrer"
                  className="text-pulse-blue hover:text-white underline transition-colors"
                >
                  Download .NET Runtime (Official MS)
                </a>
              </div>
            </div>

            <div className="flex flex-col items-stretch sm:items-end gap-2 flex-shrink-0">
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={DOWNLOAD_LINKS.ANDROID_APK}
                  download="ck-pulse-manager.apk"
                  className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center justify-center gap-2 shadow-lg shadow-emerald-950/40 transition-all hover:scale-[1.02]"
                >
                  <Smartphone className="w-4 h-4 text-white" />
                  <span>Android App (.apk)</span>
                </a>
                <a
                  href={DOWNLOAD_LINKS.WINDOWS_EXE}
                  download="CKPulse.Agent.exe"
                  className="px-4 py-2.5 rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white text-xs font-semibold flex items-center justify-center gap-2 shadow-lg shadow-pulse-blue/20 transition-all hover:scale-[1.02]"
                >
                  <Download className="w-4 h-4" />
                  <span>Windows .EXE</span>
                </a>
                <a
                  href={DOWNLOAD_LINKS.WINDOWS_ZIP}
                  download="CKPulse-Agent-v1.0.0-win-x64.zip"
                  className="px-3.5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium flex items-center justify-center gap-1.5 border border-zinc-700 transition-colors"
                  title="Download ZIP package with auto-installer script"
                >
                  <span>.ZIP Bundle</span>
                </a>
              </div>
              <span className="text-[10px] text-zinc-500 font-mono">
                ZIP includes auto-installer for .NET Runtime
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 10. ENTERPRISE FAQ ACCORDION */}
      <section className="py-16 px-4 sm:px-6 max-w-4xl mx-auto border-t border-zinc-900">
        <div className="text-center mb-10">
          <span className="text-[10px] font-mono uppercase tracking-widest text-pulse-blue font-semibold">
            Questions & Clarity
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mt-1">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-3">
          {[
            {
              q: 'How does the Windows agent connect to a specific company account?',
              a: 'Every company admin generates a secure, one-time 9-character Pairing Code (e.g., CK-TDN3-S9CJ) for each employee from their private admin dashboard. When the employee launches CKPulse.Agent.exe, they enter this code. The agent verifies it with the Cloudflare D1 database, receives a permanent cryptographic Device Key bound exclusively to that company\'s tenant ID, and securely saves it in local app storage. All subsequent events route exclusively to that company.',
            },
            {
              q: 'What should I do if Windows prompts: "You must install .NET Desktop Runtime"?',
              a: 'CK Pulse is built on native Microsoft .NET 9 for peak performance and ultra-low RAM usage (<15 MB). If your PC does not have .NET 9 yet, download the .ZIP Bundle (which includes the 1-click "Install-and-Run.bat" script that automatically sets it up) or install it directly using Microsoft\'s official 1-click installer: https://aka.ms/dotnet/9.0/windowsdesktop-runtime-win-x64.exe.',
            },
            {
              q: 'Does CK Pulse record what my employees type or see?',
              a: 'Absolutely not. CK Pulse is strictly an inactivity threshold detector. It uses the native Windows Win32 API GetLastInputInfo to determine only the number of seconds elapsed since the last mouse click or keypress. It never captures characters, words, screenshots, or webcam video.',
            },
            {
              q: 'Will running the Windows background agent slow down workstations?',
              a: 'No. The agent is built in native C# / .NET 9 and consumes less than 0.5% CPU and around 15 MB of RAM. It runs silently in the system tray and operates efficiently in the background.',
            },
            {
              q: 'How does CK Pulse relate to the CK Ecosystem?',
              a: 'CK Pulse is the hardware and workstation monitoring pillar of the CK Ecosystem (https://ck-ecosystem.pages.dev/). It utilizes shared cryptographic standards, tenant boundaries, and ecosystem single sign-on concepts.',
            },
          ].map((item, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div
                key={idx}
                className="p-5 rounded-2xl bg-zinc-950/60 border border-zinc-800/80 transition-colors"
              >
                <button
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between text-left text-sm font-semibold text-white focus:outline-none"
                >
                  <span>{item.q}</span>
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-zinc-400 shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" />
                  )}
                </button>
                {isOpen && (
                  <p className="mt-3 text-xs text-zinc-400 leading-relaxed pt-2 border-t border-zinc-900">
                    {item.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 11. FINAL CONVERSION BANNER */}
      <section className="py-16 px-4 sm:px-6 max-w-6xl mx-auto">
        <div className="glass-card rounded-3xl p-8 sm:p-14 text-center relative overflow-hidden bg-gradient-to-b from-blue-950/30 via-zinc-950 to-black">
          <div className="max-w-2xl mx-auto space-y-4">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Transform Workstation Awareness Today
            </h2>
            <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed">
              Deploy the lightweight Windows agent, configure custom inactivity rules, and protect team productivity without invading privacy.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-4">
              <a
                href="/downloads/ck-pulse-manager.apk"
                download="ck-pulse-manager.apk"
                className="w-full sm:w-auto px-6 py-3 text-xs sm:text-sm font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white transition-all flex items-center justify-center gap-2 shadow-xl shadow-emerald-950/40"
              >
                <Smartphone className="w-4 h-4 text-white" />
                <span>Download Android App (.apk)</span>
              </a>
              <a
                href="/downloads/CKPulse.Agent.exe"
                download="CKPulse.Agent.exe"
                className="w-full sm:w-auto px-6 py-3 text-xs sm:text-sm font-semibold rounded-xl bg-white text-black hover:bg-zinc-200 transition-all flex items-center justify-center gap-2 shadow-xl"
              >
                <Download className="w-4 h-4 text-black" />
                <span>Download Windows Agent (.exe)</span>
              </a>
              <Link
                to="/activate"
                className="w-full sm:w-auto px-5 py-3 text-xs sm:text-sm font-semibold rounded-xl bg-pulse-blue hover:bg-pulse-blueHover text-white shadow-xl shadow-pulse-blue/25 transition-all"
              >
                Activate Key
              </Link>
              <Link
                to="/login"
                className="w-full sm:w-auto px-5 py-3 text-xs sm:text-sm font-medium rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border border-zinc-700 transition-all"
              >
                Sign In
              </Link>
              <a
                href="https://ck-ecosystem.pages.dev/"
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto px-4 py-3 text-xs sm:text-sm font-medium rounded-xl bg-zinc-950 hover:bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800 transition-all flex items-center justify-center gap-1.5"
              >
                <span>CK Ecosystem</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 12. ENTERPRISE FOOTER */}
      <footer className="border-t border-zinc-900 py-12 px-4 sm:px-6 max-w-6xl mx-auto text-xs text-zinc-500">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={pulseLogo} alt="CK Pulse" className="w-8 h-8 object-contain drop-shadow" />
            <div>
              <div className="font-semibold text-white">CK Pulse</div>
              <div className="text-[11px] text-zinc-500">Workstation Inactivity Monitoring & Alerting</div>
            </div>
          </div>

          {/* Footer Navigation */}
          <div className="flex items-center gap-6 flex-wrap justify-center text-[11px] text-zinc-400">
            <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
            <Link to="/activate" className="hover:text-white transition-colors">Activate License</Link>
            <a
              href="https://ck-ecosystem.pages.dev/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-pulse-blue transition-colors flex items-center gap-1"
            >
              CK Ecosystem
              <ExternalLink className="w-3 h-3" />
            </a>
            <span className="text-zinc-600">|</span>
            <span className="text-zinc-500">APAC Cloudflare D1 Engine</span>
          </div>

          <div className="text-[11px] text-zinc-600 text-center md:text-right">
            © {new Date().getFullYear()} CK Pulse. Part of the{' '}
            <a
              href="https://ck-ecosystem.pages.dev/"
              target="_blank"
              rel="noreferrer"
              className="text-zinc-400 hover:text-white underline underline-offset-2"
            >
              CK Ecosystem
            </a>
            .
          </div>
        </div>
      </footer>
    </div>
  );
}
